import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, Typography, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Layout from '../../components/layout/Layout';
import PrimarySearchBar from '../../components/searchBar/searchbar';
import {
  getSeasonalMenus, addSeasonalMenu, updateSeasonalMenu, deleteSeasonalMenu,
  sendSeasonalMenu, getSeasonalOrders, upsertSeasonalOrder, updateOrderStatus,
  seasonalFlagOff,
} from '../../Redux/Actions/seasonalMenuAction';
import './seasonalmenu.css';

// ─── constants ───────────────────────────────────────────────────────────────

const MENU_STATUS = {
  draft:  { label: '초안',   bg: '#e3f2fd', color: '#1565c0' },
  sent:   { label: '발송됨', bg: '#e8f5e9', color: '#2e7d32' },
  closed: { label: '마감',   bg: '#fce4ec', color: '#c62828' },
};

const ORDER_STATUS = {
  pending:   { label: '신청대기', bg: '#fff8e1', color: '#f57f17' },
  confirmed: { label: '승인',     bg: '#e8f5e9', color: '#2e7d32' },
  rejected:  { label: '거절',     bg: '#fce4ec', color: '#c62828' },
};

const emptyItem = {
  name: '',
  hot12Price: '', hot12Cal: '',
  hot16Price: '', hot16Cal: '',
  hot20Price: '', hot20Cal: '',
  iced16Price: '', iced16Cal: '',
  iced20Price: '', iced20Cal: '',
};

const emptyMenuForm = {
  name: '', description: '', startDate: '', endDate: '',
  items: [{ ...emptyItem }],
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('ko-KR');
}

function fmtPrice(n) {
  if (n === undefined || n === null || n === '') return '-';
  return `₩${Number(n).toLocaleString()}`;
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

function StatusBadge({ map, value }) {
  const cfg = map[value] || { label: value, bg: '#eee', color: '#555' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ background: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 11 }}
    />
  );
}

// ─── CampaignMenuDialog ───────────────────────────────────────────────────────

const PRICE_FIELD = { type: 'number', style: { width: 70 } };
const CAL_FIELD   = { type: 'text',   style: { width: 56 } };

function PriceCell({ label, name, value, onChange }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
      <Typography sx={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap' }}>{label}</Typography>
      <input
        name={name}
        value={value}
        onChange={onChange}
        placeholder="-"
        style={{
          width: 62, border: '1px solid #e0e0e0', borderRadius: 6,
          padding: '4px 6px', fontSize: 12, textAlign: 'center',
          outline: 'none', background: 'white',
        }}
      />
    </Box>
  );
}

function CampaignMenuDialog({ open, onClose, editTarget, onSubmit }) {
  const [form, setForm] = useState(emptyMenuForm);

  useEffect(() => {
    if (open) {
      if (editTarget) {
        setForm({
          name: editTarget.name || '',
          description: editTarget.description || '',
          startDate: editTarget.startDate ? editTarget.startDate.substring(0, 10) : '',
          endDate: editTarget.endDate ? editTarget.endDate.substring(0, 10) : '',
          items: (editTarget.items && editTarget.items.length > 0)
            ? editTarget.items.map(it => ({
                name:        it.name || '',
                hot12Price:  it.hot12Price  !== undefined ? String(it.hot12Price)  : (it.basePrice ? String(it.basePrice) : ''),
                hot12Cal:    it.hot12Cal    || '',
                hot16Price:  it.hot16Price  !== undefined ? String(it.hot16Price)  : '',
                hot16Cal:    it.hot16Cal    || '',
                hot20Price:  it.hot20Price  !== undefined ? String(it.hot20Price)  : '',
                hot20Cal:    it.hot20Cal    || '',
                iced16Price: it.iced16Price !== undefined ? String(it.iced16Price) : '',
                iced16Cal:   it.iced16Cal   || '',
                iced20Price: it.iced20Price !== undefined ? String(it.iced20Price) : '',
                iced20Cal:   it.iced20Cal   || '',
              }))
            : [{ ...emptyItem }],
        });
      } else {
        setForm({ ...emptyMenuForm, items: [{ ...emptyItem }] });
      }
    }
  }, [open, editTarget]);

  const handleField = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleItemField = (idx, e) => {
    const items = form.items.map((it, i) =>
      i === idx ? { ...it, [e.target.name]: e.target.value } : it
    );
    setForm(f => ({ ...f, items }));
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));

  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = () => {
    const obj = {
      ...form,
      items: form.items.map(it => ({
        name:        it.name,
        basePrice:   Number(it.hot12Price) || 0,
        hot12Price:  Number(it.hot12Price)  || 0,
        hot12Cal:    it.hot12Cal,
        hot16Price:  Number(it.hot16Price)  || 0,
        hot16Cal:    it.hot16Cal,
        hot20Price:  Number(it.hot20Price)  || 0,
        hot20Cal:    it.hot20Cal,
        iced16Price: Number(it.iced16Price) || 0,
        iced16Cal:   it.iced16Cal,
        iced20Price: Number(it.iced20Price) || 0,
        iced20Cal:   it.iced20Cal,
      })),
    };
    onSubmit(obj);
  };

  const canSubmit = form.name.trim() && form.items.every(it => it.name.trim());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#551FFF' }}>
        {editTarget ? '시즌 캠페인 수정' : '시즌 캠페인 추가'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth label="캠페인 이름 *" name="name"
              value={form.name} onChange={handleField} size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="설명" name="description"
              value={form.description} onChange={handleField}
              size="small" multiline rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="시작일" name="startDate" type="date"
              value={form.startDate} onChange={handleField}
              size="small" InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="종료일" name="endDate" type="date"
              value={form.endDate} onChange={handleField}
              size="small" InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Items section */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333' }}>
                메뉴 항목
              </Typography>
              <button
                type="button"
                onClick={addItem}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '4px 12px', border: 'none', borderRadius: 8,
                  background: '#551FFF', color: 'white', fontSize: 12,
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                <AddIcon sx={{ fontSize: 14 }} /> 항목 추가
              </button>
            </Box>

            {/* Column header */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto',
              background: '#f5f3ff', borderRadius: '8px 8px 0 0',
              px: 1.5, py: 0.8, mb: 0,
              border: '1px solid #e8e4ff', borderBottom: 'none',
            }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#551FFF' }}>메뉴명</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box sx={{
                  background: '#ff6b35', color: 'white', borderRadius: 1,
                  px: 1.5, py: 0.2, fontSize: 11, fontWeight: 700, mr: 0.5,
                  display: 'flex', alignItems: 'center',
                }}>
                  HOT &nbsp;·&nbsp; 12oz / 16oz / 20oz
                </Box>
                <Box sx={{
                  background: '#0ea5e9', color: 'white', borderRadius: 1,
                  px: 1.5, py: 0.2, fontSize: 11, fontWeight: 700,
                  display: 'flex', alignItems: 'center',
                }}>
                  ICED &nbsp;·&nbsp; 16oz / 20oz
                </Box>
              </Box>
            </Box>

            {form.items.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  border: '1px solid #e8e4ff',
                  borderTop: idx === 0 ? '1px solid #e8e4ff' : 'none',
                  background: 'white',
                  borderRadius: idx === form.items.length - 1 ? '0 0 8px 8px' : 0,
                  px: 1.5, py: 1.2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
                  {/* 메뉴명 */}
                  <Box sx={{ flex: '1 1 140px', minWidth: 120 }}>
                    <Typography sx={{ fontSize: 10, color: '#888', mb: 0.3 }}>메뉴명 *</Typography>
                    <input
                      name="name"
                      value={item.name}
                      onChange={e => handleItemField(idx, e)}
                      placeholder="메뉴명"
                      style={{
                        width: '100%', border: '1px solid #e0e0e0', borderRadius: 6,
                        padding: '5px 8px', fontSize: 13, outline: 'none',
                      }}
                    />
                  </Box>

                  {/* HOT */}
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box sx={{
                      background: '#fff5f0', border: '1px solid #ffcbb3',
                      borderRadius: 1.5, px: 1, py: 0.5,
                      display: 'flex', gap: 0.5, alignItems: 'flex-start',
                    }}>
                      <PriceCell label="HOT 12oz" name="hot12Price" value={item.hot12Price} onChange={e => handleItemField(idx, e)} />
                      <PriceCell label="CAL"       name="hot12Cal"   value={item.hot12Cal}   onChange={e => handleItemField(idx, e)} />
                      <Box sx={{ width: '1px', background: '#ffd0b5', alignSelf: 'stretch', mx: 0.3 }} />
                      <PriceCell label="HOT 16oz" name="hot16Price" value={item.hot16Price} onChange={e => handleItemField(idx, e)} />
                      <PriceCell label="CAL"       name="hot16Cal"   value={item.hot16Cal}   onChange={e => handleItemField(idx, e)} />
                      <Box sx={{ width: '1px', background: '#ffd0b5', alignSelf: 'stretch', mx: 0.3 }} />
                      <PriceCell label="HOT 20oz" name="hot20Price" value={item.hot20Price} onChange={e => handleItemField(idx, e)} />
                      <PriceCell label="CAL"       name="hot20Cal"   value={item.hot20Cal}   onChange={e => handleItemField(idx, e)} />
                    </Box>

                    {/* ICED */}
                    <Box sx={{
                      background: '#f0f9ff', border: '1px solid #bae6fd',
                      borderRadius: 1.5, px: 1, py: 0.5,
                      display: 'flex', gap: 0.5, alignItems: 'flex-start',
                    }}>
                      <PriceCell label="ICED 16oz" name="iced16Price" value={item.iced16Price} onChange={e => handleItemField(idx, e)} />
                      <PriceCell label="CAL"        name="iced16Cal"   value={item.iced16Cal}   onChange={e => handleItemField(idx, e)} />
                      <Box sx={{ width: '1px', background: '#bae6fd', alignSelf: 'stretch', mx: 0.3 }} />
                      <PriceCell label="ICED 20oz" name="iced20Price" value={item.iced20Price} onChange={e => handleItemField(idx, e)} />
                      <PriceCell label="CAL"        name="iced20Cal"   value={item.iced20Cal}   onChange={e => handleItemField(idx, e)} />
                    </Box>
                  </Box>

                  {/* Remove button */}
                  {form.items.length > 1 && (
                    <IconButton size="small" onClick={() => removeItem(idx)} sx={{ color: '#c62828', mt: 1.8 }}>
                      <RemoveIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: '#888' }}>취소</Button>
        <Button
          variant="contained" onClick={handleSubmit} disabled={!canSubmit}
          sx={{ background: '#551FFF', borderRadius: 2 }}
        >
          {editTarget ? '저장' : '추가'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── OrderEditDialog ──────────────────────────────────────────────────────────

function OrderEditDialog({ open, onClose, store, selectedMenu, existingOrder, onSave }) {
  const [participating, setParticipating] = useState(true);
  const [itemPrices, setItemPrices] = useState([]);
  const [memo, setMemo] = useState('');

  useEffect(() => {
    if (open && selectedMenu) {
      const items = (selectedMenu.items || []);
      if (existingOrder) {
        setParticipating(existingOrder.participating !== false);
        setMemo(existingOrder.memo || '');
        // build itemPrices from existing order, falling back to basePrice
        const merged = items.map(it => {
          const found = (existingOrder.itemPrices || []).find(ip => ip.itemName === it.name);
          return {
            itemName: it.name,
            basePrice: it.basePrice || 0,
            finalPrice: found ? String(found.finalPrice) : String(it.basePrice || 0),
          };
        });
        setItemPrices(merged);
      } else {
        setParticipating(true);
        setMemo('');
        setItemPrices(items.map(it => ({
          itemName: it.name,
          basePrice: it.basePrice || 0,
          finalPrice: String(it.basePrice || 0),
        })));
      }
    }
  }, [open, selectedMenu, existingOrder]);

  const handlePriceChange = (idx, val) => {
    setItemPrices(prev => prev.map((ip, i) => i === idx ? { ...ip, finalPrice: val } : ip));
  };

  const handleSave = () => {
    const prices = itemPrices.map(ip => ({
      itemName: ip.itemName,
      basePrice: ip.basePrice,
      finalPrice: Number(ip.finalPrice) || ip.basePrice,
    }));
    onSave({ participating, itemPrices: prices, memo });
  };

  const storeName = store?.tljNo || store?.ownerName || store?.name || '점포';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#551FFF' }}>
        점포 신청 {existingOrder ? '수정' : '등록'} — {storeName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {/* 판매 여부 */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>판매 여부</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <button
              type="button"
              onClick={() => setParticipating(true)}
              className={`filter-btn${participating ? ' filter-btn--active' : ''}`}
              style={{ padding: '8px 20px', fontSize: 14, fontWeight: 700 }}
            >
              판매
            </button>
            <button
              type="button"
              onClick={() => setParticipating(false)}
              className={`filter-btn${!participating ? ' filter-btn--active filter-btn--no-sale' : ''}`}
              style={{ padding: '8px 20px', fontSize: 14, fontWeight: 700 }}
            >
              판매 안함
            </button>
          </Box>

          {/* 아이템별 가격 */}
          {participating && itemPrices.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>항목별 판매가</Typography>
              {itemPrices.map((ip, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, mb: 1.5,
                    p: 1.5, background: '#fafafa', borderRadius: 2, border: '1px solid #eee',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{ip.itemName}</Typography>
                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                      기준가: {fmtPrice(ip.basePrice)}
                    </Typography>
                  </Box>
                  <TextField
                    label="판매가 (₩)" type="number"
                    value={ip.finalPrice}
                    onChange={e => handlePriceChange(idx, e.target.value)}
                    size="small"
                    sx={{ width: 140 }}
                  />
                </Box>
              ))}
            </>
          )}

          {/* 메모 */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, mt: 1 }}>메모</Typography>
          <TextField
            fullWidth multiline rows={2} size="small"
            placeholder="전달사항 (선택)"
            value={memo} onChange={e => setMemo(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: '#888' }}>취소</Button>
        <Button
          variant="contained" onClick={handleSave}
          sx={{ background: '#551FFF', borderRadius: 2 }}
        >
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function SeasonalMenu({ user }) {
  const dispatch = useDispatch();
  const { menus, orders, isMenuAdded, isMenuUpdated, isMenuDeleted, isSent } =
    useSelector(s => s.SeasonalMenu);
  const { stores } = useSelector(s => s.Store);

  // left panel state
  const [textVal, setTextVal] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);

  // dialogs
  const [menuDialog, setMenuDialog] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sendConfirm, setSendConfirm] = useState(null);
  const [orderDialog, setOrderDialog] = useState(false);
  const [orderDialogStore, setOrderDialogStore] = useState(null);
  const [orderDialogExisting, setOrderDialogExisting] = useState(null);

  // filter
  const [statusFilter, setStatusFilter] = useState('all');

  // ── effects ──
  useEffect(() => { if (user?._id) dispatch(getSeasonalMenus(user._id)); }, [user]);
  useEffect(() => { setFiltered([...menus]); }, [menus]);
  useEffect(() => {
    if (isMenuAdded || isMenuUpdated) {
      dispatch(seasonalFlagOff());
      setMenuDialog(false);
      setEditTarget(null);
    }
    if (isMenuDeleted || isSent) dispatch(seasonalFlagOff());
  }, [isMenuAdded, isMenuUpdated, isMenuDeleted, isSent]);

  useEffect(() => {
    if (selectedMenu) {
      dispatch(getSeasonalOrders(selectedMenu._id));
      setStatusFilter('all');
    }
  }, [selectedMenu]);

  // keep selectedMenu in sync when menus list updates
  useEffect(() => {
    if (selectedMenu) {
      const updated = menus.find(m => m._id === selectedMenu._id);
      if (updated) setSelectedMenu(updated);
    }
  }, [menus]);

  // ── search ──
  const handleSearch = (val) => {
    setTextVal(val);
    const q = val.toLowerCase();
    setFiltered(!val ? [...menus] : menus.filter(m =>
      m.name?.toLowerCase().includes(q) || m.description?.toLowerCase().includes(q)
    ));
  };

  // ── campaign CRUD ──
  const openAdd = () => { setEditTarget(null); setMenuDialog(true); };
  const openEdit = (m) => { setEditTarget(m); setMenuDialog(true); };

  const handleMenuSubmit = (obj) => {
    if (editTarget) dispatch(updateSeasonalMenu(editTarget._id, { ...obj, user: user._id }));
    else dispatch(addSeasonalMenu({ ...obj, user: user._id }));
  };

  const handleSend = (menuId) => {
    dispatch(sendSeasonalMenu(menuId, user._id));
    setSendConfirm(null);
  };

  // ── order status ──
  const handleOrderStatus = (orderId, status, store) => {
    const email = store?.ownerEmail || store?.managerEmail || '';
    dispatch(updateOrderStatus(orderId, status, email));
  };

  // ── order edit dialog ──
  const openOrderDialog = (store, existingOrder) => {
    setOrderDialogStore(store);
    setOrderDialogExisting(existingOrder || null);
    setOrderDialog(true);
  };

  const handleOrderSave = ({ participating, itemPrices, memo }) => {
    const storeId = orderDialogStore?._id;
    const menuId = selectedMenu?._id;
    if (!storeId || !menuId) return;
    dispatch(upsertSeasonalOrder({ storeId, menuId, participating, itemPrices, memo }));
    setOrderDialog(false);
  };

  // ── computed ──
  const activeStores = (stores || []).filter(s => s.status === 'active');
  const activeStoreCount = activeStores.length;

  // join stores with orders for the table
  const tableRows = activeStores.map(store => {
    const order = orders.find(o => {
      const sid = o.store?._id || o.store;
      return String(sid) === String(store._id);
    });
    return { store, order };
  });

  // stats
  const statCounts = {
    all: tableRows.length,
    pending: tableRows.filter(r => r.order && r.order.participating !== false && r.order.status === 'pending').length,
    confirmed: tableRows.filter(r => r.order && r.order.participating !== false && r.order.status === 'confirmed').length,
    rejected: tableRows.filter(r => r.order && r.order.participating !== false && r.order.status === 'rejected').length,
    noResponse: tableRows.filter(r => !r.order).length,
    noSale: tableRows.filter(r => r.order && r.order.participating === false).length,
  };

  // filtered rows
  const visibleRows = tableRows.filter(({ order }) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'noResponse') return !order;
    if (statusFilter === 'noSale') return order && order.participating === false;
    if (!order || order.participating === false) return false;
    return order.status === statusFilter;
  });

  // items from selected menu
  const menuItems = selectedMenu?.items || [];

  return (
    <Layout title="Seasonal Menu" user={user}>
      <Box sx={{ p: 2 }}>
        <div className="seasonal-layout">

          {/* ── LEFT PANEL ── */}
          <div className="seasonal-left">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#551FFF', fontSize: 14 }}>
                시즌 캠페인
              </Typography>
              <button className="sm-btn sm-btn--primary" onClick={openAdd}>
                <AddIcon sx={{ fontSize: 13 }} /> 시즌 추가
              </button>
            </Box>

            <Box sx={{ mb: 1.5, background: 'white', borderRadius: '12px', px: 1 }}>
              <PrimarySearchBar value={textVal} onChange={e => handleSearch(e.target.value)} />
            </Box>
            {textVal && (
              <button
                onClick={() => handleSearch('')}
                style={{ marginBottom: 8, padding: '4px 12px', border: 'none', borderRadius: 8, background: '#eee', cursor: 'pointer', fontSize: 12 }}
              >
                초기화
              </button>
            )}

            {filtered.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: '#aaa', background: 'white', borderRadius: 2, fontSize: 13 }}>
                등록된 캠페인이 없습니다.
              </Box>
            )}

            {filtered.map(m => {
              const mOrders = orders; // we only have orders for selected, used for stats on selected
              const itemCount = (m.items || []).length;
              const isSelected = selectedMenu?._id === m._id;

              return (
                <div
                  key={m._id}
                  className={`campaign-card${isSelected ? ' campaign-card--selected' : ''}`}
                  onClick={() => setSelectedMenu(m)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                    <div className="campaign-card-name">{m.name}</div>
                    <StatusBadge map={MENU_STATUS} value={m.status} />
                  </div>
                  <div className="campaign-card-meta">
                    항목 {itemCount}개
                    {m.startDate && (
                      <> · {m.startDate.substring(0, 10)} ~ {m.endDate?.substring(0, 10)}</>
                    )}
                  </div>
                  {m.description && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {m.description}
                    </div>
                  )}
                  {isSelected && statCounts.all > 0 && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>
                      응답 {statCounts.all - statCounts.noResponse}/{statCounts.all}
                    </div>
                  )}
                  {m.sentAt && (
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                      발송: {fmtDate(m.sentAt)} · {m.sentCount}명
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── RIGHT PANEL ── */}
          <div className="seasonal-right">
            {!selectedMenu ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280, background: 'white', borderRadius: 3, color: '#bbb' }}>
                <Typography>← 왼쪽에서 캠페인을 선택하면 현황이 표시됩니다</Typography>
              </Box>
            ) : (
              <Box>
                {/* ── HEADER ── */}
                <Box sx={{ background: 'white', borderRadius: 3, p: 2, mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                          {selectedMenu.name}
                        </Typography>
                        <StatusBadge map={MENU_STATUS} value={selectedMenu.status} />
                      </Box>
                      {(selectedMenu.startDate || selectedMenu.endDate) && (
                        <Typography variant="caption" sx={{ color: '#888', mt: 0.3, display: 'block' }}>
                          {fmtDate(selectedMenu.startDate)} ~ {fmtDate(selectedMenu.endDate)}
                        </Typography>
                      )}
                      {selectedMenu.description && (
                        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                          {selectedMenu.description}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Tooltip title="전체 점포에 이메일 발송">
                        <button
                          className="sm-btn sm-btn--send"
                          onClick={() => setSendConfirm(selectedMenu._id)}
                          disabled={activeStoreCount === 0}
                        >
                          <SendIcon sx={{ fontSize: 13 }} /> 발송
                        </button>
                      </Tooltip>
                      <button className="sm-btn sm-btn--edit" onClick={() => openEdit(selectedMenu)}>
                        <EditIcon sx={{ fontSize: 13 }} /> 수정
                      </button>
                      <button className="sm-btn sm-btn--delete" onClick={() => setDeleteConfirm(selectedMenu._id)}>
                        <DeleteOutlineIcon sx={{ fontSize: 13 }} /> 삭제
                      </button>
                    </Box>
                  </Box>

                  {/* ── ITEM CHIPS ── */}
                  {menuItems.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                      {menuItems.map((item, idx) => (
                        <div key={idx} className="item-chip">
                          <span style={{ fontWeight: 600 }}>{item.name}</span>
                          {item.hot12Price > 0 && (
                            <span style={{ marginLeft: 6, color: '#ff6b35', fontWeight: 700, fontSize: 11 }}>
                              HOT {fmtPrice(item.hot12Price)}
                              {item.hot16Price > 0 && `/${fmtPrice(item.hot16Price)}`}
                            </span>
                          )}
                          {item.iced16Price > 0 && (
                            <span style={{ marginLeft: 4, color: '#0ea5e9', fontWeight: 700, fontSize: 11 }}>
                              ICED {fmtPrice(item.iced16Price)}
                            </span>
                          )}
                          {!item.hot12Price && !item.iced16Price && item.basePrice > 0 && (
                            <span style={{ marginLeft: 6, color: '#551FFF', fontWeight: 700 }}>
                              {fmtPrice(item.basePrice)}
                            </span>
                          )}
                        </div>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* ── STATS BAR ── */}
                <div className="campaign-stats-bar">
                  <button
                    className={`filter-btn${statusFilter === 'all' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('all')}
                  >
                    전체 <span style={{ fontWeight: 800 }}>{statCounts.all}</span>
                  </button>
                  <button
                    className={`filter-btn${statusFilter === 'pending' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('pending')}
                    style={{ color: '#f57f17' }}
                  >
                    신청대기 <span style={{ fontWeight: 800 }}>{statCounts.pending}</span>
                  </button>
                  <button
                    className={`filter-btn${statusFilter === 'confirmed' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('confirmed')}
                    style={{ color: '#2e7d32' }}
                  >
                    승인 <span style={{ fontWeight: 800 }}>{statCounts.confirmed}</span>
                  </button>
                  <button
                    className={`filter-btn${statusFilter === 'rejected' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('rejected')}
                    style={{ color: '#c62828' }}
                  >
                    거절 <span style={{ fontWeight: 800 }}>{statCounts.rejected}</span>
                  </button>
                  <button
                    className={`filter-btn${statusFilter === 'noResponse' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('noResponse')}
                    style={{ color: '#999' }}
                  >
                    미응답 <span style={{ fontWeight: 800 }}>{statCounts.noResponse}</span>
                  </button>
                  <button
                    className={`filter-btn${statusFilter === 'noSale' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('noSale')}
                    style={{ color: '#c62828' }}
                  >
                    판매안함 <span style={{ fontWeight: 800 }}>{statCounts.noSale}</span>
                  </button>
                </div>

                {/* ── STORE TABLE ── */}
                <TableContainer
                  component={Paper}
                  sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', mt: 1.5 }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: '#f5f3ff' }}>
                        <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>점포</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 90 }}>판매여부</TableCell>
                        {menuItems.map((item, idx) => (
                          <TableCell key={idx} sx={{ fontWeight: 700, minWidth: 110 }}>
                            {item.name}
                            <br />
                            <span style={{ fontSize: 10, color: '#aaa', fontWeight: 400 }}>
                              기준 {fmtPrice(item.basePrice)}
                            </span>
                          </TableCell>
                        ))}
                        <TableCell sx={{ fontWeight: 700, minWidth: 110 }}>메모</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 80 }}>상태</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>관리</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeStores.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5 + menuItems.length} align="center" sx={{ py: 4, color: '#aaa' }}>
                            운영 중인 점포가 없습니다. Store 페이지에서 점포를 추가하세요.
                          </TableCell>
                        </TableRow>
                      )}
                      {visibleRows.map(({ store, order }) => {
                        const isNoResponse = !order;
                        const isNoSale = order && order.participating === false;

                        let rowBg = 'white';
                        if (isNoResponse) rowBg = '#fafafa';
                        else if (isNoSale) rowBg = '#fff5f5';
                        else if (order?.status === 'confirmed') rowBg = '#f1fff4';
                        else if (order?.status === 'rejected') rowBg = '#fff5f5';

                        return (
                          <TableRow
                            key={store._id}
                            hover
                            sx={{ background: rowBg }}
                          >
                            {/* 점포 */}
                            <TableCell>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>
                                {store.tljNo || store.ownerName || '-'}
                              </div>
                              <div style={{ fontSize: 11, color: '#888' }}>{store.address}</div>
                            </TableCell>

                            {/* 판매여부 */}
                            <TableCell>
                              {isNoResponse ? (
                                <span className="no-response">미응답</span>
                              ) : isNoSale ? (
                                <Chip label="판매안함" size="small" sx={{ background: '#fce4ec', color: '#c62828', fontWeight: 700, fontSize: 11 }} />
                              ) : (
                                <Chip label="판매" size="small" sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: 11 }} />
                              )}
                            </TableCell>

                            {/* 아이템별 판매가 */}
                            {menuItems.map((item, idx) => {
                              if (isNoResponse || isNoSale) {
                                return (
                                  <TableCell key={idx}>
                                    <span className="price-base">-</span>
                                  </TableCell>
                                );
                              }
                              const ip = (order?.itemPrices || []).find(p => p.itemName === item.name);
                              if (!ip) {
                                return (
                                  <TableCell key={idx}>
                                    <span className="price-base">{fmtPrice(item.basePrice)}</span>
                                  </TableCell>
                                );
                              }
                              const isChanged = Number(ip.finalPrice) !== Number(ip.basePrice);
                              return (
                                <TableCell key={idx}>
                                  <span className={isChanged ? 'price-changed' : 'price-base'}>
                                    {fmtPrice(ip.finalPrice)}
                                  </span>
                                  {isChanged && (
                                    <div style={{ fontSize: 10, color: '#aaa' }}>
                                      기준 {fmtPrice(ip.basePrice)}
                                    </div>
                                  )}
                                </TableCell>
                              );
                            })}

                            {/* 메모 */}
                            <TableCell sx={{ maxWidth: 140 }}>
                              <div style={{ fontSize: 12, color: '#555', wordBreak: 'break-all' }}>
                                {order?.memo || '-'}
                              </div>
                            </TableCell>

                            {/* 상태 */}
                            <TableCell>
                              {isNoResponse ? (
                                <span className="no-response" style={{ fontSize: 12 }}>미응답</span>
                              ) : isNoSale ? (
                                <span style={{ fontSize: 12, color: '#c62828' }}>판매안함</span>
                              ) : (
                                <StatusBadge map={ORDER_STATUS} value={order.status} />
                              )}
                            </TableCell>

                            {/* 관리 */}
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {!isNoResponse && !isNoSale && order?.status !== 'confirmed' && (
                                  <Tooltip title="승인 + 이메일 발송">
                                    <button
                                      className="order-action-btn order-action-btn--approve"
                                      onClick={() => handleOrderStatus(order._id, 'confirmed', store)}
                                    >
                                      <CheckCircleOutlineIcon sx={{ fontSize: 13 }} /> 승인
                                    </button>
                                  </Tooltip>
                                )}
                                {!isNoResponse && !isNoSale && order?.status !== 'rejected' && (
                                  <Tooltip title="거절 + 이메일 발송">
                                    <button
                                      className="order-action-btn order-action-btn--reject"
                                      onClick={() => handleOrderStatus(order._id, 'rejected', store)}
                                    >
                                      <CancelOutlinedIcon sx={{ fontSize: 13 }} /> 거절
                                    </button>
                                  </Tooltip>
                                )}
                                <Tooltip title={isNoResponse ? '새 신청 등록' : '수정'}>
                                  <button
                                    className="order-action-btn order-action-btn--edit"
                                    onClick={() => openOrderDialog(store, order)}
                                  >
                                    <EditIcon sx={{ fontSize: 13 }} /> 수정
                                  </button>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {visibleRows.length === 0 && activeStores.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={5 + menuItems.length} align="center" sx={{ py: 3, color: '#aaa' }}>
                            해당 필터에 해당하는 점포가 없습니다.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 1, fontSize: 11, color: '#aaa' }}>
                  * 승인/거절 시 점주 이메일로 자동 알림이 발송됩니다.
                </Box>
              </Box>
            )}
          </div>
        </div>

        {/* ── DIALOGS ── */}

        {/* Campaign add/edit */}
        <CampaignMenuDialog
          open={menuDialog}
          onClose={() => { setMenuDialog(false); setEditTarget(null); }}
          editTarget={editTarget}
          onSubmit={handleMenuSubmit}
        />

        {/* Order edit */}
        <OrderEditDialog
          open={orderDialog}
          onClose={() => setOrderDialog(false)}
          store={orderDialogStore}
          selectedMenu={selectedMenu}
          existingOrder={orderDialogExisting}
          onSave={handleOrderSave}
        />

        {/* Delete confirm */}
        <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
          <DialogTitle>시즌 캠페인 삭제</DialogTitle>
          <DialogContent>
            삭제하면 관련 신청 내역도 모두 삭제됩니다. 계속하시겠습니까?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirm(null)}>취소</Button>
            <Button
              color="error" variant="contained"
              onClick={() => {
                dispatch(deleteSeasonalMenu(deleteConfirm));
                if (selectedMenu?._id === deleteConfirm) setSelectedMenu(null);
                setDeleteConfirm(null);
              }}
            >
              삭제
            </Button>
          </DialogActions>
        </Dialog>

        {/* Send confirm */}
        <Dialog open={!!sendConfirm} onClose={() => setSendConfirm(null)}>
          <DialogTitle sx={{ color: '#551FFF', fontWeight: 700 }}>전체 점포 이메일 발송</DialogTitle>
          <DialogContent>
            <Typography>
              운영 중인 <strong>{activeStoreCount}개</strong> 점포의 점주/점장 이메일로<br />
              시즌 메뉴 신청 안내 메일을 발송합니다.<br /><br />
              계속하시겠습니까?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSendConfirm(null)}>취소</Button>
            <Button
              variant="contained"
              onClick={() => handleSend(sendConfirm)}
              sx={{ background: '#551FFF', borderRadius: 2 }}
            >
              발송
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Layout>
  );
}

export default SeasonalMenu;
